import clsx from 'clsx';
import React from 'react';
import { useParams } from 'react-router';

import { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import ModalPortal from '@/src/components/common/Modal/ModalPortal';
import { isResponseError } from '@/src/components/project/Inbox2/api/config';
import ConfirmArchiveDocumentModal from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/components/SidebarActions/DeleteFile/ArchiveDocumentModal/ConfirmArchiveDocumentModal/ConfirmArchiveDocumentModal';
import ActionButton from '@/src/components/project/Inbox2/Elements/ActionButton/ActionButton';
import { useInboxEntryPanel } from '@/src/components/project/Inbox2/store/inboxEntryPanel.store';
import { EmailEntry } from '@/src/components/project/Inbox2/types';
import useInboxSearchParams from '@/src/components/project/Inbox2/useInboxSearchParams';
import { showToast } from '@/src/globalFunction';
import { useToggle } from '@/src/hooks/useToggle';

import useSyncInboxEntry from '../../../../../useSyncInboxEntry';
import { useValidateActionResponse } from '../../../../../useValidateActionResponse';
import { getCurrentEmailEntriesCountFromCache } from '../../../../api/getEmailEntries';
import { useMarkDeleteEmail } from '../../../../api/markDeleteEmail';
import { useSyncSelectedEmailEntryListItem } from '../../EmailEntries/useSyncSelectedEmailEntryListItem';

const subject = 'email';

type DeleteFileProps = {
  selectedEmailEntry: EmailEntry;
  enabled: boolean;
};

const DeleteEmail = ({ selectedEmailEntry, enabled }: DeleteFileProps) => {
  const [showConfirmArchiveModal, toggleShowConfirmArchiveModal] = useToggle();
  const syncEmailEntries = useSyncSelectedEmailEntryListItem({ emailEntry: selectedEmailEntry });
  const { projectId } = useParams<{ projectId: string }>();
  const deleteEmailMutation = useMarkDeleteEmail({
    projectId,
    inboxEntryId: selectedEmailEntry.inboxEntryId,
  });
  const { confirmRefreshModal } = useValidateActionResponse({
    responseError: isResponseError(deleteEmailMutation.data) ? deleteEmailMutation.data : null,
  });

  const { setInboxEntry } = useInboxEntryPanel();
  const { filter } = useInboxSearchParams();
  const syncInboxEntry = useSyncInboxEntry();

  const handleClick = () => {
    toggleShowConfirmArchiveModal();
  };

  const handleClose = () => {
    toggleShowConfirmArchiveModal(false);
  };

  const handleDelete = async () => {
    const response = await deleteEmailMutation.mutateAsync({
      projectId,
      inboxEntryId: selectedEmailEntry.inboxEntryId,
      emailId: selectedEmailEntry.id,
      trash: true,
    });

    if (isResponseError(response)) {
      return;
    }

    if (response) {
      handleClose();
      const currentEmailEntriesCount = getCurrentEmailEntriesCountFromCache({
        projectId,
        filter,
        inboxEntryId: selectedEmailEntry.inboxEntryId,
      });

      if (currentEmailEntriesCount <= 1) {
        setInboxEntry(null);
      } else {
        syncEmailEntries();
        syncInboxEntry(response.data['inbox-entry']);
      }

      showToast('success', 'Successfully deleted the entry.');
    }
  };

  return (
    <React.Fragment>
      {deleteEmailMutation.isLoading && <Spinner />}
      <ActionButton
        id={'delete_file'}
        className={clsx('tw-text-cfo-red-400', 'hover:tw-bg-red-100')}
        icon={ICONS.delete}
        iconClasses={clsx('tw-text-cfo-red-400', 'group-hover:tw-text-cfo-red-400')}
        onClick={handleClick}
        tooltipSettings={{
          content: 'Delete',
          place: 'left',
        }}
        disabled={!enabled}
      />

      {showConfirmArchiveModal && (
        <ModalPortal>
          <ConfirmArchiveDocumentModal
            subject={subject}
            bodySubject={subject}
            onClose={handleClose}
            onSubmit={handleDelete}
          />
        </ModalPortal>
      )}
      {confirmRefreshModal}
    </React.Fragment>
  );
};

export default DeleteEmail;
